import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Zap, Home, Users, AlertTriangle, ShieldCheck, Send } from 'lucide-react';
import { toast } from 'sonner';
import DocumentUploader from '../components/DocumentUploader';
import CategoryMultiSelect from '../components/CategoryMultiSelect';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function QuickQuotation() {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [policy, setPolicy] = useState(null);
  const [type, setType] = useState('society');
  const [form, setForm] = useState({
    title: '', work_category: '', location: '', estimated_value: '',
    scope_of_work: '', flat_number: '', owner_name: '',
  });
  const [consent, setConsent] = useState([]);
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [l, p] = await Promise.allSettled([
        axios.get(`${API}/buyer/quick-quotations`, { withCredentials: true }),
        axios.get(`${API}/buyer/policy/active`, { withCredentials: true }),
      ]);
      if (l.status === 'fulfilled') setList(l.value.data || []);
      if (p.status === 'fulfilled') setPolicy(p.value.data);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title) return toast.error('Title is required');
    if (!form.work_category) return toast.error('Please select a Work Category');
    if (type === 'flat_owner') {
      if (!form.flat_number || !form.owner_name || consent.length === 0) return toast.error('Flat number, owner name and consent letter required');
      if (!accepted) return toast.error('Please accept the facilitator disclaimer');
    }
    setBusy(true);
    try {
      const payload = {
        type, title: form.title, work_category: form.work_category, location: form.location,
        estimated_value: form.estimated_value ? parseFloat(form.estimated_value) : null,
        scope_of_work: form.scope_of_work,
      };
      if (type === 'flat_owner') {
        payload.flat_number = form.flat_number;
        payload.owner_name = form.owner_name;
        payload.consent_letter = consent[0];
        payload.disclaimer_accepted = true;
      }
      const { data } = await axios.post(`${API}/buyer/quick-quotation`, payload, { withCredentials: true });
      const notif = data?.notifications;
      if (notif && notif.matched_count > 0) {
        toast.success(`Quick Quotation created — ${notif.matched_count} matching supplier${notif.matched_count > 1 ? 's' : ''} notified.`);
      } else {
        toast.success('Quick Quotation created — no matching suppliers found in this category yet.');
      }
      setForm({ title: '', work_category: '', location: '', estimated_value: '', scope_of_work: '', flat_number: '', owner_name: '' });
      setConsent([]); setAccepted(false);
      await load();
    } catch (er) { toast.error(er.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const limit = policy?.quick_quotation_limit || 0;
  const valNum = parseFloat(form.estimated_value || 0);
  const exceedsLimit = type === 'society' && valNum > 0 && valNum > limit;

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-navy text-white">
        <nav className="container mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <button onClick={() => navigate('/buyer-dashboard')} className="text-white/70 hover:text-white inline-flex items-center" data-testid="qq-back-btn">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back
          </button>
          <div className="font-bold flex items-center gap-2"><Zap className="h-5 w-5 text-gold" /> Quick Quotation</div>
          <div />
        </nav>
      </header>

      <div className="container mx-auto px-4 sm:px-6 py-6 max-w-4xl">
        {/* Type toggle */}
        <div className="grid sm:grid-cols-2 gap-3 mb-5">
          <button onClick={() => setType('society')} className={`p-4 rounded-lg border-2 text-left transition-colors ${type === 'society' ? 'border-orange bg-orange/5' : 'border-border'}`} data-testid="qq-type-society">
            <Users className="h-5 w-5 text-navy mb-1" />
            <div className="font-bold text-navy text-sm">Society Procurement (Type A)</div>
            <div className="text-xs text-muted-foreground">Society funds. Policy-gated. Minimum quotation count enforced.</div>
          </button>
          <button onClick={() => setType('flat_owner')} className={`p-4 rounded-lg border-2 text-left transition-colors ${type === 'flat_owner' ? 'border-orange bg-orange/5' : 'border-border'}`} data-testid="qq-type-flat">
            <Home className="h-5 w-5 text-navy mb-1" />
            <div className="font-bold text-navy text-sm">Flat Owner Assistance (Type B)</div>
            <div className="text-xs text-muted-foreground">Society facilitates only. No society funds. Consent letter required.</div>
          </button>
        </div>

        {/* Policy status (society only) */}
        {type === 'society' && (
          <div className={`mb-4 p-3 rounded border ${policy ? 'border-green-300 bg-green-50/30' : 'border-orange/40 bg-orange/5'} flex items-center gap-2 text-sm`} data-testid="qq-policy-status">
            <ShieldCheck className={`h-4 w-4 ${policy ? 'text-green-600' : 'text-orange'}`} />
            {policy ? `Policy v${policy.version} · Quick Quotation limit ₹${Number(limit).toLocaleString('en-IN')} · Min ${policy.minimum_quotations_required} quotations` : 'No active policy — Quick Quotations cannot be raised'}
          </div>
        )}

        {/* Form */}
        <form onSubmit={submit} className="bg-card border border-border rounded-lg p-5 space-y-3" data-testid="qq-form">
          <Input label="Title *" value={form.title} onChange={v => setForm({ ...form, title: v })} testid="qq-title" />
          <div className="grid sm:grid-cols-2 gap-3">
            <div data-testid="qq-category-wrapper">
              <CategoryMultiSelect
                label="Work Category *"
                multiple={false}
                allowOther
                required
                testid="qq-category"
                value={form.work_category ? [form.work_category] : []}
                onChange={(arr) => setForm({ ...form, work_category: arr[0] || '' })}
                placeholder="Select a work category…"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input label="Location" value={form.location} onChange={v => setForm({ ...form, location: v })} testid="qq-location" />
              <Input label="Estimated Procurement Budget (₹)" type="number" value={form.estimated_value} onChange={v => setForm({ ...form, estimated_value: v })} testid="qq-value" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-navy mb-1">Scope of Work</label>
            <textarea value={form.scope_of_work} onChange={(e) => setForm({ ...form, scope_of_work: e.target.value })} rows={3} className="w-full px-3 py-2 rounded border border-input bg-background text-sm" data-testid="qq-scope" />
          </div>

          {exceedsLimit && (
            <div className="p-3 rounded border-2 border-orange bg-orange/10 text-xs text-navy flex items-start gap-2" data-testid="qq-exceeds-warning">
              <AlertTriangle className="h-4 w-4 text-orange flex-shrink-0 mt-0.5" />
              <div>Procurement exceeds approved Quick Quotation limit (₹{Number(limit).toLocaleString('en-IN')}). Please use Full Tender Workflow.</div>
            </div>
          )}

          {type === 'flat_owner' && (
            <div className="border-t border-border pt-4 space-y-3" data-testid="qq-flat-owner-block">
              <h3 className="font-bold text-navy text-sm">Flat Owner Details</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                <Input label="Flat Number *" value={form.flat_number} onChange={v => setForm({ ...form, flat_number: v })} testid="qq-flat-number" />
                <Input label="Owner Name *" value={form.owner_name} onChange={v => setForm({ ...form, owner_name: v })} testid="qq-owner-name" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-navy mb-1">Flat Owner Consent Letter *</label>
                <DocumentUploader files={consent} onChange={setConsent} purpose="flat_owner_consent" maxFiles={1} />
              </div>
              <label className="flex items-start gap-2 text-xs text-foreground/80 p-3 rounded border border-border bg-muted/30" data-testid="qq-disclaimer-block">
                <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} className="mt-0.5" data-testid="qq-disclaimer-accept" />
                <span>
                  <strong>Disclaimer (mandatory):</strong> This quotation request is being facilitated on behalf of a flat owner. The Housing Society and ProcureOne Ultra act only as facilitators. Vendor selection, contract execution, quality, payment and disputes remain solely between the flat owner and vendor.
                </span>
              </label>
            </div>
          )}

          <button type="submit" disabled={busy || exceedsLimit} className="px-5 py-2 rounded bg-orange text-white text-sm font-bold disabled:opacity-40 inline-flex items-center" data-testid="qq-submit-btn">
            <Send className="h-4 w-4 mr-2" /> {busy ? 'Saving…' : 'Create Quick Quotation'}
          </button>
        </form>

        {/* Existing list */}
        {list.length > 0 && (
          <div className="mt-6">
            <h2 className="font-bold text-navy mb-2">Recent Quick Quotations</h2>
            <div className="space-y-2">
              {list.slice(0, 10).map(qq => (
                <div key={qq.id} className="p-3 rounded border border-border bg-card text-xs flex items-center justify-between" data-testid={`qq-row-${qq.id}`}>
                  <div>
                    <div className="font-semibold text-navy">{qq.title}</div>
                    <div className="text-muted-foreground">
                      {qq.estimated_value != null ? `₹${Number(qq.estimated_value).toLocaleString('en-IN')}` : 'Budget: Not Available'} · {qq.type === 'society' ? 'Society' : 'Flat Owner'}
                      {qq.policy_version ? ` · Policy v${qq.policy_version}` : ''}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="uppercase text-[10px] font-bold text-orange">{qq.status.replace(/_/g, ' ')}</div>
                    <a
                      href={`/buyer/negotiation/quick_quotation/${qq.id}`}
                      className="text-[10px] font-bold text-navy underline hover:text-orange"
                      data-testid={`qq-open-negotiation-${qq.id}`}
                    >
                      Negotiation →
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const Input = ({ label, value, onChange, type = 'text', testid }) => (
  <div>
    <label className="block text-xs font-semibold text-navy mb-1">{label}</label>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2 rounded border border-input bg-background text-sm" data-testid={testid} />
  </div>
);
